/** Position of a rule in its kind's evaluation order, with Move up / Move down (rules.reorder). */

import type { Rule, RuleId, RuleKind } from '@digester/core';
import { reportError } from '../../app/errors.js';
import { useEngine } from '../../engine-client/EngineProvider.js';
import { Icon } from '../../icons/Icon.js';
import { Section } from '../../ui/form.js';

export interface OrderSectionProps {
  kind: RuleKind;
  rule: Rule;
  /** rules.list(kind): live in order, then archived. */
  rules: readonly Rule[];
  help: string;
}

export function OrderSection({ kind, rule, rules, help }: OrderSectionProps) {
  const engine = useEngine();
  const order = rules.filter((r) => r.archivedAt === null).map((r) => r.id);
  const index = order.indexOf(rule.id);
  if (index < 0) return null;
  const move = async (delta: number): Promise<void> => {
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    const next: RuleId[] = [...order];
    [next[index], next[target]] = [next[target] as RuleId, next[index] as RuleId];
    try {
      await engine.rules.reorder(kind, next);
    } catch (error) {
      reportError(error);
    }
  };
  return (
    <Section title="Order">
      <div className="order-row" data-order={index + 1}>
        <strong>
          Position {index + 1} of {order.length}
        </strong>
        <button type="button" className="secondary-button" disabled={index === 0} onClick={() => void move(-1)}>
          <Icon name="arrow-up" /> Move up
        </button>
        <button type="button" className="secondary-button" disabled={index >= order.length - 1} onClick={() => void move(1)}>
          <Icon name="arrow-down" /> Move down
        </button>
      </div>
      <p className="field-help">{help}</p>
    </Section>
  );
}

/** An archived rule: restorable (it returns at the end of the order). */
export function ArchivedNote({ onRestore }: { onRestore(): void }) {
  return (
    <div className="editor-note archived-note">
      <span>Archived. It does not run; restoring puts it back at the end of the order.</span>
      <button type="button" className="secondary-button" onClick={onRestore}>
        <Icon name="rotate-ccw" /> Restore
      </button>
    </div>
  );
}
